import { useState, useEffect, useMemo } from "react";
import { apiGet } from "../api/client";
import { ErrorBanner, useApiError } from "../components/ErrorBanner";
import {
  Calendar,
  AlertTriangle,
  Filter,
  RefreshCw,
  Clock,
} from "lucide-react";

interface CalendarEvent {
  event_id: string;
  event_ts: string;
  country: string;
  kind: string;
  importance: "HIGH" | "MEDIUM" | "LOW";
  title: string;
  detail: { content?: string } | null;
  source: string;
}

const KIND_LABELS: Record<string, string> = {
  ADP_EMPLOYMENT: "ADP 민간고용",
  FOMC_MINUTES: "FOMC 의사록",
  PRESS_CONFERENCE: "기자회견",
  BEIGE_BOOK: "베이지북",
  OIL_INVENTORY: "원유 재고",
  JOBLESS_CLAIMS: "실업수당 청구",
  FOMC: "FOMC",
  RATE_DECISION: "기준금리",
  CPI: "CPI",
  PPI: "PPI",
  NFP: "NFP",
  ECI: "고용비용",
  GDP: "GDP",
  PMI: "PMI",
  UNEMPLOYMENT: "실업률",
  CONSUMER_SENTIMENT: "소비자심리",
  INFLATION_EXPECTATIONS: "기대인플레이션",
  TRADE_BALANCE: "무역수지",
  CURRENT_ACCOUNT: "경상수지",
  RETAIL_SALES: "소매",
  DURABLE_GOODS: "내구재",
  HOUSING: "주택",
  ISM: "ISM",
  TREASURY_AUCTION: "채권입찰",
  HOLIDAY: "휴장/공휴일",
  OTHER: "기타",
};

const IMPORTANCE_COLORS: Record<string, string> = {
  HIGH: "var(--red)",
  MEDIUM: "var(--orange)",
  LOW: "var(--text-muted)",
};

function timeUntil(ts: string): { text: string; isPast: boolean } {
  const now = new Date();
  const target = new Date(ts);
  const diffMs = target.getTime() - now.getTime();
  if (diffMs < 0) return { text: "종료", isPast: true };
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(hours / 24);
  if (days > 0) return { text: `D-${days}`, isPast: false };
  if (hours > 0) return { text: `${hours}시간 후`, isPast: false };
  const mins = Math.floor(diffMs / 60000);
  return { text: `${mins}분 후`, isPast: false };
}

function formatKST(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Seoul",
  });
}

export default function EconomicCalendar() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [collection, setCollection] = useState<{state?: string; last_success_at?: string; stale?: boolean} | null>(null);
  const [daysAhead, setDaysAhead] = useState(30);
  const [daysBack, setDaysBack] = useState(7);
  const [filterImportance, setFilterImportance] = useState<string>("ALL");
  const [filterCountry, setFilterCountry] = useState<string>("ALL");
  const { error, setError, wrap } = useApiError();

  const fetchEvents = () =>
    wrap(async () => {
      setLoading(true);
      let params = `days_ahead=${daysAhead}&days_back=${daysBack}&limit=500`;
      if (filterImportance !== "ALL") params += `&importance=${filterImportance}`;
      if (filterCountry !== "ALL") params += `&country=${filterCountry}`;
      const data = await apiGet<{ events: CalendarEvent[]; collection?: typeof collection }>(`/calendar/events?${params}`);
      setEvents(data.events || []);
      setCollection(data.collection || null);
      setLoading(false);
    });

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 60000);
    return () => clearInterval(interval);
  }, [daysAhead, daysBack, filterImportance, filterCountry]);

  // Group events by date (KST date)
  const grouped = useMemo(() => {
    const groups: Record<string, CalendarEvent[]> = {};
    for (const ev of events) {
      const dateKey = new Date(ev.event_ts).toLocaleDateString("ko-KR", {
        month: "long",
        day: "numeric",
        weekday: "short",
        timeZone: "Asia/Seoul",
      });
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(ev);
    }
    return groups;
  }, [events]);

  const highImpactCount = events.filter((e) => e.importance === "HIGH").length;

  // Detect market holiday for today
  const todayHoliday = useMemo(() => {
    const todayKst = new Date().toLocaleDateString("ko-KR", {
      month: "long",
      day: "numeric",
      timeZone: "Asia/Seoul",
    });
    return events.find((ev) => {
      const evDate = new Date(ev.event_ts).toLocaleDateString("ko-KR", {
        month: "long",
        day: "numeric",
        timeZone: "Asia/Seoul",
      });
      const isHoliday =
        ev.kind === "HOLIDAY" ||
        ev.title.includes("휴장") ||
        ev.title.includes("노동절");
      return evDate === todayKst && isHoliday;
    });
  }, [events]);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>
              <Calendar size={22} color="var(--accent)" />
              <span>경제 캘린더 (Economic Calendar)</span>
            </h2>
            <p>
              SaveTicker API 기반 글로벌 거시경제 일정 · 총 {events.length}개 이벤트 (고영향 {highImpactCount}개)
            </p>
          </div>
          <button className="btn-secondary" onClick={fetchEvents} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} />
            새로고침
          </button>
        </div>
      </div>

      <ErrorBanner message={error} onDismiss={() => setError(null)} />
      <p className="data-method-note">일정·뉴스는 SaveTicker를 최우선 기준으로 사용합니다. 수집 상태 {collection?.state || '미확인'} · 마지막 성공 {collection?.last_success_at ? `${formatKST(collection.last_success_at)} KST` : '미관측'}.</p>
      {collection && (collection.state !== 'ok' || collection.stale) && <p className="research-alert">일정 수집 실패 또는 지연 · 저장된 일정을 표시합니다. 빈 목록은 일정 부재의 확인이 아닙니다.</p>}

      {/* Market Holiday Notice Banner */}
      {todayHoliday && (
        <div
          className="card"
          style={{
            backgroundColor: "rgba(245, 158, 11, 0.1)",
            borderColor: "var(--yellow)",
            marginBottom: "var(--space-4)",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <AlertTriangle size={18} color="var(--yellow)" />
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            <strong>글로벌 시장 휴장 안내:</strong> 오늘은{" "}
            <strong>{todayHoliday.title}</strong>으로{" "}
            {todayHoliday.country === "US"
              ? "미국 증권거래소(NYSE/NASDAQ) 및 채권 시장이 전면 휴장"
              : "해당 국가 금융시장이 휴장"}
            합니다. 정규 경제지표 발표 일정이 제한될 수 있습니다.
          </div>
        </div>
      )}

      {/* Filters Toolbar */}
      <div className="card" style={{ marginBottom: "var(--space-4)", padding: "var(--space-3) var(--space-4)" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-muted)", fontSize: "var(--text-sm)", fontWeight: 700 }}>
            <Filter size={14} /> 필터:
          </div>

          <label>
            향후 일정:
            <select
              value={daysAhead}
              onChange={(e) => setDaysAhead(Number(e.target.value))}
            >
              <option value={7}>향후 7일</option>
              <option value={14}>향후 14일</option>
              <option value={30}>향후 30일</option>
              <option value={60}>향후 60일</option>
              <option value={90}>향후 90일</option>
            </select>
          </label>

          <label>
            과거 지표 포함:
            <select
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
            >
              <option value={7}>최근 7일 포함</option>
              <option value={14}>최근 14일 포함</option>
              <option value={30}>최근 30일 포함</option>
              <option value={0}>향후 일정만</option>
            </select>
          </label>

          <label>
            중요도:
            <select
              value={filterImportance}
              onChange={(e) => setFilterImportance(e.target.value)}
            >
              <option value="ALL">전체 (All)</option>
              <option value="HIGH">고위험 (HIGH)</option>
              <option value="MEDIUM">중위험 (MEDIUM)</option>
              <option value="LOW">일반 (LOW)</option>
            </select>
          </label>

          <label>
            국가:
            <select
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
            >
              <option value="ALL">전체 국가</option>
              <option value="US">미국 (US)</option>
              <option value="KR">한국 (KR)</option>
              <option value="JP">일본 (JP)</option>
              <option value="EU">유럽 (EU)</option>
              <option value="GB">영국 (GB)</option>
              <option value="CN">중국 (CN)</option>
            </select>
          </label>
        </div>
      </div>

      {/* Event list grouped by date */}
      {loading && events.length === 0 ? (
        <div className="card" style={{ padding: "40px 20px", textAlign: "center", color: "var(--text-muted)" }}>
          경제 캘린더 일정 로딩 중...
        </div>
      ) : events.length === 0 ? (
        <div className="card" style={{ padding: "40px 20px", textAlign: "center", color: "var(--text-muted)", fontStyle: "italic" }}>
          {collection?.state === 'ok' && !collection.stale ? '선택한 조건에 해당하는 SaveTicker 저장 일정이 없습니다.' : '최신 일정 수집 상태를 확인할 수 없습니다.'}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {Object.entries(grouped).map(([dateKey, dayEvents]) => (
            <div key={dateKey}>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: "var(--text-secondary)",
                  marginBottom: 8,
                  paddingBottom: 4,
                  borderBottom: "1px solid var(--border)",
                  letterSpacing: "-0.01em",
                }}
              >
                {dateKey}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {dayEvents.map((ev) => {
                  const countdown = timeUntil(ev.event_ts);
                  return (
                    <article key={ev.event_id} className={`card calendar-event${countdown.isPast ? ' calendar-event-past' : ''}`} style={{ borderLeftColor: IMPORTANCE_COLORS[ev.importance] }}>
                      <div className="calendar-event-time"><Clock size={14}/><time dateTime={ev.event_ts}>{formatKST(ev.event_ts)} KST</time></div>
                      <span className={`badge calendar-countdown ${!countdown.isPast && ev.importance === 'HIGH' ? 'badge-red' : ''}`}>{countdown.text}</span>
                      <span className="badge">{ev.country}</span>
                      <span className="badge badge-blue">{KIND_LABELS[ev.kind] || ev.kind}</span>
                      <div className="calendar-event-title">{ev.importance === 'HIGH' && <AlertTriangle size={14}/>}<strong>{ev.title}</strong><span className="data-unit">중요도 {ev.importance}</span></div>
                      <span className="calendar-event-source">출처 {ev.source === 'saveticker_api' ? 'SaveTicker' : ev.source === 'fomc_official' ? 'FOMC' : 'YAML'}</span>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
